<template>
  <v-dialog
      v-model="dialog"
      :width="dialogWidth"
      @keydown.enter.prevent="onenter"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Tutorial ({{ this.rules.name }})</span>
        <v-spacer/>
        <r-authorize
            :master="masterAccess"
            :locked.sync="model.locked"
            :enabled.sync="model.enabled"
        />
      </v-card-title>
      <v-card-text>
        <v-col cols="4" sm="2" md="2">
          <v-btn @click="showPreview()" class="mr-2" small>View JSON</v-btn>
        </v-col>
        <v-row dense>
          <v-col cols="12" md="12" sm="12" class="mb-n5">
            <v-text-field
                label="ID:"
                v-model.trim="model.id"
                v-ascii-only
                outlined
                dense
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6" sm="6">
            <v-text-field
                label="Name:"
                v-model.trim="model.name"
                v-ascii-only
            />
          </v-col>
          <v-col cols="12" md="6" sm="6">
            <v-text-field
                label="Key:"
                v-model.trim="model.key"
                v-ascii-only
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="3" sm="3">
            <v-checkbox v-model="model.v2" label="V2" hide-details/>
          </v-col>
          <v-col cols="12" md="3" sm="3">
            <v-checkbox v-model="model.master" label="Master" hide-details/>
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <r-tutorial-dropdown :model.sync="model.showNext" label="Show Next" :clearable="true"/>
          </v-col>
          <v-col cols="12" md="12" sm="12" class="mt-3">
            <v-divider/>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6" sm="6">
            <r-tutorial-kind :model.sync="model.kind" label="Type:"/>
          </v-col>
          <v-col cols="12" md="2" sm="2">
            <v-text-field label="Delay Show" v-model.number="model.delayOnShow" hide-details/>
          </v-col>
          <v-col cols="12" md="2" sm="2">
            <v-text-field label="Auto Close" v-model.number="model.autoClose" hide-details/>
          </v-col>
          <v-col cols="12" md="2" sm="2">
            <v-text-field label="Priority" v-model.number="model.priority" hide-details/>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="4" sm="4">
            <v-checkbox :disabled="!model.v2" v-model="model.lightMode" label="Light Mode" hide-details/>
          </v-col>
          <v-col cols="12" md="4" sm="4">
            <v-checkbox v-model="model.strict" label="Strict Mode" hide-details/>
          </v-col>
          <v-col cols="12" md="4" sm="4">
            <v-checkbox v-model="model.camera" label="Focus Camera" hide-details/>
          </v-col>
          <v-col cols="12" md="4" sm="4">
            <v-checkbox v-model="model.ignorePopup" label="Ignore Popup" hide-details/>
          </v-col>
          <v-col cols="12" md="4" sm="4">
            <v-checkbox v-model="model.overNotice" label="Over Notice" hide-details/>
          </v-col>
          <v-col cols="12" md="4" sm="4">
            <v-checkbox v-model="model.shared" label="Shared" hide-details/>
          </v-col>
          <v-col cols="12" md="4" sm="4">
            <v-checkbox :disabled="!model.v2" v-model="model.blockedByV1" label="Blocked by V1" hide-details/>
          </v-col>
          <v-col cols="12" md="4" sm="4">
            <v-checkbox v-model="model.hidesDialogs" label="Hides Dialogs" hide-details/>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="12" sm="12"><br></v-col>
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header v-slot="{ open }">
                <v-row no-gutters>
                  <span style="margin-top: 5px">Configs</span>
                  <v-spacer/>
                  <v-fade-transition>
                    <span style="display: inline-flex" v-if="open">
                      <v-divider class="mx-1" vertical/>
                    </span>
                  </v-fade-transition>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" sm="6" md="6">
                    <r-item-dropdown :model.sync="model.item"/>
                  </v-col>
                  <v-col cols="12" md="12" sm="12">
                    <r-cell-config-table :items.sync="model.cells" title="Cells"/>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <r-guard-panel :model.sync="model.guard" title="Guard Config"/>
            <r-trigger-panel :items.sync="model.triggersAvailable" title="Triggers - Available"/>
            <r-trigger-panel :items.sync="model.triggersComplete" title="Triggers - Complete"/>
            <r-panel title="Map Group">
              <r-map-group-dropdown :model.sync="model.mapGroup"/>
            </r-panel>
          </v-expansion-panels>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>
          Cancel
        </v-btn>
        <v-btn @click="save" color="blue darken-1" text v-if="!lockedAssess || masterAccess">
          Save
        </v-btn>
      </v-card-actions>
      <div class="resize-handle" @mousedown.stop.prevent="startResize"></div>
      <r-preview-dialog ref="preview-dialog"></r-preview-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import VueTypes from "vue-types";
import {ViewRules} from "@/common/ViewRules";
import {TutorialModel} from "@/modules/redactor/models/tutorial/TutorialModel";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";

import RTriggerPanel from "@/modules/redactor/views/trigger/r-trigger-panel";
import RTutorialKind from "@/modules/redactor/views/tutorial/r-tutorial-kind";
import RCellConfigTable from "@/modules/redactor/views/cells/r-cell-config-table";
import RAuthorize from "@/modules/redactor/views/common/r-authorize";
import RItemDropdown from "@/modules/redactor/views/item/r-item-dropdown";
import RGuardPanel from "@/modules/redactor/views/guard/r-guard-panel";
import RTutorialDropdown from "@/modules/redactor/views/tutorial/r-tutorial-dropdown.vue";
import RPreviewDialog from "@/modules/redactor/views/preview/r-preview-dialog.vue";
import RPanel from "@/modules/redactor/views/utils/r-panel.vue";
import RMapGroupDropdown from "@/modules/redactor/views/map-group/r-map-group-dropdown.vue";

export default {
  name: "r-tutorial-dialog",
  components: {RPreviewDialog, RTutorialDropdown, RGuardPanel, RItemDropdown, RAuthorize, RCellConfigTable, RTutorialKind, RTriggerPanel, RPanel, RMapGroupDropdown},
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: VueTypes.objectOf(TutorialModel),
    rules: ViewRules.READONLY,

    dialogWidth: 575,
    dialogHeight: 876,

    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 575,
    resizeStartHeight: 876,
    viewportPadding: 32 // kept for parity, not used for max clamps
  }),
  computed: {
    lockedAssess() {
      return this.model.locked;
    },
    masterAccess() {
      return RedactorUtils.hasMasterAccess();
    }
  },
  methods: {
    show(rules, model) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new TutorialModel();

      if (ViewRules.COPY.equal(this.rules)) {
        this.model.locked = false;
      }

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    save() {
      if (this.model.valid()) {
        this.resolve && this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },
    cancel() {
      this.reject && this.reject();
      this.discard();
    },
    discard() {
      this.dialog = false;
      this.stopResize();
    },
    onenter() {
      if (!this.lockedAssess || this.masterAccess) {
        this.save();
      } else {
        this.cancel();
      }
    },

    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;

      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (!this.resizing) return;

      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;

      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;

      const minWidth = 400;
      const minHeight = 400;

      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;

      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },
    stopResize() {
      if (!this.resizing) return;

      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },
    showPreview() {
      this.$refs['preview-dialog'].show(this.rules, this.model, TutorialModel)
          .then((result) => {
            Object.assign(this.model, result);
          })
          .catch((e) => {
            this.$alert("Failed to parse config!" + e.message);
          });
    }
  },
  beforeDestroy() {
    this.stopResize();
  }
};
</script>

<style lang="css" scoped>
:read-only {
  white-space: nowrap;
}

.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}

.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}

.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>